/*
  Trautis Shoppable Image: hotspot markers over a hero photo.
  Desktop hover reveal is pure CSS. JS adds touch behaviour
  (tap-to-toggle, one card open, Esc / outside-click close) as
  progressive enhancement. Pure helpers exported for testability.
*/

(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    root.TrautisShoppable = api;
    if (root.document && root.document.addEventListener) {
      if (root.document.readyState === 'loading') {
        root.document.addEventListener('DOMContentLoaded', api.init);
      } else {
        api.init();
      }
    }
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // ===== Pure helpers (testable) ===========================================

  // Decide how a card should be anchored so it stays inside the image.
  function computeCardPlacement(x, y) {
    return { flipLeft: Number(x) > 66, below: Number(y) < 25 };
  }

  // How many px to shift an opened card horizontally so it stays inside the
  // image bounds (with a gutter). Anchoring via flip classes is coarse — a
  // left-edge hotspot's centered card still spills off the left on mobile —
  // so this clamps the final box. Pins the left edge if the card is too wide
  // to fit, keeping the label/thumbnail visible.
  function computeNudge(cardRect, boundsRect, gutter) {
    var g = gutter || 0;
    var min = boundsRect.left + g;
    var max = boundsRect.right - g;
    if (cardRect.left < min) return min - cardRect.left;     // overflow left  -> shift right
    if (cardRect.right > max) return max - cardRect.right;    // overflow right -> shift left
    return 0;
  }

  // State machine: tracks the single open hotspot, drives DOM via `apply`.
  function createController(apply) {
    var openId = null;
    function close() {
      if (openId !== null) { apply(openId, false); openId = null; }
    }
    function open(id) {
      if (openId === id) return;
      close();
      apply(id, true);
      openId = id;
    }
    function toggle(id) { if (openId === id) close(); else open(id); }
    function isOpen(id) { return openId === id; }
    return {
      open: open, close: close, toggle: toggle, isOpen: isOpen,
      get openId() { return openId; }
    };
  }

  // True on devices with a real hover + fine pointer (desktop).
  function supportsHover(matchMediaFn) {
    var mm = matchMediaFn || (typeof window !== 'undefined' && window.matchMedia);
    if (!mm) return false;
    return !!mm('(hover: hover) and (pointer: fine)').matches;
  }

  // ===== DOM wiring (browser only) =========================================

  function makeApply(rootEl) {
    return function apply(id, isOpen) {
      var spot = rootEl.querySelector('[data-spot][data-id="' + id + '"]');
      if (!spot) return;
      spot.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      var card = document.getElementById(spot.getAttribute('aria-controls'));
      if (!card) return;
      if (isOpen) {
        var place = computeCardPlacement(spot.getAttribute('data-x'), spot.getAttribute('data-y'));
        card.classList.toggle('is-flip-left', place.flipLeft);
        card.classList.toggle('is-below', place.below);
        card.style.setProperty('--t-nudge', '0px'); // reset before measuring
        card.hidden = false;
        card.classList.add('is-open');
        // Clamp horizontally so the card never spills past the image edges.
        var img = rootEl.querySelector('.t-shoppable__img');
        if (img && card.getBoundingClientRect) {
          var nudge = computeNudge(card.getBoundingClientRect(), img.getBoundingClientRect(), 8);
          card.style.setProperty('--t-nudge', nudge + 'px');
        }
      } else {
        card.hidden = true;
        card.classList.remove('is-open');
        card.style.setProperty('--t-nudge', '0px');
      }
    };
  }

  function bindTouch(rootEl) {
    var ctrl = createController(makeApply(rootEl));
    var spots = rootEl.querySelectorAll('[data-spot]');
    Array.prototype.forEach.call(spots, function (spot) {
      spot.addEventListener('click', function (e) {
        e.preventDefault();
        ctrl.toggle(spot.getAttribute('data-id'));
      });
    });
    document.addEventListener('click', function (e) {
      if (!rootEl.contains(e.target)) ctrl.close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') ctrl.close();
    });
  }

  function init() {
    if (typeof document === 'undefined') return;
    if (supportsHover()) return; // desktop: CSS handles hover, no JS needed
    var roots = document.querySelectorAll('[data-shoppable]');
    Array.prototype.forEach.call(roots, function (rootEl) {
      if (rootEl.dataset && rootEl.dataset.bound === '1') return;
      if (rootEl.dataset) rootEl.dataset.bound = '1';
      bindTouch(rootEl);
    });
  }

  return {
    computeCardPlacement: computeCardPlacement,
    computeNudge: computeNudge,
    createController: createController,
    supportsHover: supportsHover,
    init: init
  };
});
